import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faUsers, faPaperPlane, faSpinner, faExclamationTriangle, faSearch, faChevronDown, faLink
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { QuestionnaireTemplate } from '../../types/questionnaire';
import { Supplier } from '../../types/supplier';
import { bulkSendQuestionnaires, checkDuplicateSubmissions, getBulkSendStatus } from '../../services/submissionApi';
import { getSuppliers } from '../../services/api';
import { BulkSendData, DuplicateWarning, ExtendedBulkSendData, ConformityDeclarationConfig, EmergencyContactConfig } from '../../types/submission';
import DuplicateWarningModal from '../submissions/DuplicateWarningModal';
import ConformityDeclarationSection from '../submissions/ConformityDeclarationSection';
import EmergencyContactSection from '../submissions/EmergencyContactSection';
import { useToast } from '../ui/ToastProvider';
import LoadingSpinner from '../ui/LoadingSpinner';

interface SendModalProps {
  template: QuestionnaireTemplate;
  onClose: () => void;
  onSuccess?: () => void;
}

interface SupplierItemProps {
  supplier: Supplier;
  isSelected: boolean;
  onToggle: () => void;
  allSuppliers: Supplier[];
}

const SupplierItem: React.FC<SupplierItemProps> = ({ supplier, isSelected, onToggle, allSuppliers }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Finde alle Lieferanten, die mit diesem Lieferanten verknüpft sind
  const linkedSuppliers = allSuppliers.filter(s => 
    s.masterSupplierId === supplier._id // Lieferanten, die diesen als Master haben
  );

  const hasLinkedSuppliers = linkedSuppliers.length > 0;

  return (
    <div className="border border-gray-200 rounded">
      <label className="flex items-center p-2 hover:bg-gray-50 cursor-pointer">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          className="mr-2 h-3 w-3 text-purple-600"
        />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-gray-900 truncate">{supplier.name}</div>
          <div className="text-xs text-gray-600 truncate">{supplier.email}</div>
          {supplier.kreditorNumber && (
            <div className="text-xs text-gray-500 truncate">Kreditor: {supplier.kreditorNumber}</div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <div className={`px-1.5 py-0.5 text-xs rounded font-medium ${
            supplier.isActive 
              ? 'bg-green-100 text-green-700' 
              : 'bg-red-100 text-red-700'
          }`}>
            {supplier.isActive ? 'Aktiv' : 'Inaktiv'}
          </div>
          {hasLinkedSuppliers && (
            <>
              <div className="px-1.5 py-0.5 text-xs rounded font-medium bg-blue-100 text-blue-700">
                {linkedSuppliers.length} verknüpft
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <FontAwesomeIcon 
                  icon={faChevronDown} 
                  className={`text-xs transition-transform duration-200 ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </>
          )}
        </div>
      </label>
      
      {isExpanded && hasLinkedSuppliers && (
        <div className="border-t border-gray-100 p-2 bg-gray-25">
          <div className="flex items-center mb-2">
            <FontAwesomeIcon icon={faLink} className="text-gray-400 mr-1 text-xs" />
            <span className="text-xs font-medium text-gray-700">Verknüpfte Lieferanten</span>
          </div>
          
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {linkedSuppliers.map((linkedSupplier) => (
              <div key={linkedSupplier._id} className="flex items-center justify-between p-1.5 bg-white rounded border border-gray-100">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-800 truncate">
                    {linkedSupplier.name}
                  </div>
                  <div className="text-xs text-gray-600 truncate">
                    {linkedSupplier.email}
                  </div>
                  {linkedSupplier.kreditorNumber && (
                    <div className="text-xs text-gray-500 truncate">
                      Kreditor: {linkedSupplier.kreditorNumber}
                    </div>
                  )}
                </div>
                <div className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                  linkedSupplier.isActive 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {linkedSupplier.isActive ? 'Aktiv' : 'Inaktiv'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SendModal: React.FC<SendModalProps> = ({
  template,
  onClose,
  onSuccess
}) => {
  // **FILTER OUT LINKED SUPPLIERS - ONLY SHOW PRIMARY ACCOUNTS (masterSupplierId-basiert)**
  const filterPrimarySuppliers = (suppliers: Supplier[]): Supplier[] => {
    // A supplier is considered "primary" if:
    // 1. It has no masterSupplierId (masterSupplierId is null/undefined)
    // 2. This means it's not linked to another supplier
    
    const primarySuppliers = suppliers.filter(supplier => 
      !supplier.masterSupplierId // Only suppliers without masterSupplierId are primary
    );
    
    console.log(`📋 [SendModal] Filtered suppliers based on masterSupplierId: ${suppliers.length} total → ${primarySuppliers.length} primary accounts`);
    console.log(`🔗 [SendModal] Excluded ${suppliers.length - primarySuppliers.length} linked suppliers (with masterSupplierId)`);
    
    return primarySuppliers;
  };

  const { showSuccess, showError } = useToast();
  
  // **NEUE STATES FÜR SUPPLIER LOADING UND SUCHE**
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const safeSuppliers = Array.isArray(suppliers) ? filterPrimarySuppliers(suppliers) : [];
  
  const [formData, setFormData] = useState<BulkSendData>({
    supplierIds: [],
    questionnaireTemplateId: template._id || '',
    deadline: '',
    language: 'de',
    notes: '',
    assignedTo: ''
  });
  
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ processed: number; total: number } | null>(null);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  
  // **NEW: Duplicate checking states**
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateWarnings, setDuplicateWarnings] = useState<DuplicateWarning[]>([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  
  // **NEW: Conformity declaration state**
  const [conformityConfig, setConformityConfig] = useState<ConformityDeclarationConfig>({
    enabled: false,
    deadline: ''
  });

  // **NEW: Emergency contact state**
  const [emergencyContactConfig, setEmergencyContactConfig] = useState<EmergencyContactConfig>({
    enabled: false,
    deadline: ''
  });

  // **SUPPLIER LADEN BEIM ÖFFNEN DES MODALS**
  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        setSuppliersLoading(true);
        console.log('🔄 [SendModal] Loading suppliers...');
        
        // Load all suppliers for search functionality (no limit parameter)
        const response = await getSuppliers({ limit: 10000 }); // High limit to get all suppliers
        const suppliersData = response.data;
        
        let allSuppliers: Supplier[] = [];
        if (Array.isArray(suppliersData)) {
          allSuppliers = suppliersData;
        } else if (suppliersData && Array.isArray(suppliersData.suppliers)) {
          allSuppliers = suppliersData.suppliers;
        } else {
          console.warn('Suppliers data is not an array:', suppliersData);
          allSuppliers = [];
        }
        
        setSuppliers(allSuppliers);
        console.log(`✅ [SendModal] Suppliers loaded: ${allSuppliers.length}`);
        
      } catch (error) {
        console.error('❌ [SendModal] Error loading suppliers:', error);
        showError('Fehler beim Laden der Lieferanten', 'Die Lieferanten konnten nicht geladen werden');
        setSuppliers([]);
      } finally {
        setSuppliersLoading(false);
      }
    };

    loadSuppliers();
  }, [showError]);

  // **GEFILTERTE SUPPLIER MIT SUCHFUNKTION - ALLE SUPPLIERS DURCHSUCHBAR, ABER NUR 20 ANGEZEIGT**
  const filteredSuppliers = useCallback(() => {
    let filtered = safeSuppliers;
    
    // Apply search filter to all suppliers
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = safeSuppliers.filter(supplier => 
        supplier.name.toLowerCase().includes(searchLower) ||
        supplier.email.toLowerCase().includes(searchLower) ||
        (supplier.kreditorNumber && supplier.kreditorNumber.toLowerCase().includes(searchLower))
      );
    }
    
    // Return only first 20 for display performance, but all are searchable
    return filtered.slice(0, 20);
  }, [safeSuppliers, searchTerm]);

  // **SEPARATE FUNCTION FOR GETTING ALL FILTERED SUPPLIERS (FOR SELECT ALL FUNCTIONALITY)**
  const getAllFilteredSuppliers = useCallback(() => {
    if (!searchTerm) return safeSuppliers;
    
    const searchLower = searchTerm.toLowerCase();
    return safeSuppliers.filter(supplier => 
      supplier.name.toLowerCase().includes(searchLower) ||
      supplier.email.toLowerCase().includes(searchLower) ||
      (supplier.kreditorNumber && supplier.kreditorNumber.toLowerCase().includes(searchLower))
    );
  }, [safeSuppliers, searchTerm]);

  const handleSupplierToggle = (supplierId: string) => {
    setSelectedSuppliers(prev => 
      prev.includes(supplierId)
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const handleSelectAll = () => {
    // Use ALL filtered suppliers, not just the displayed 20
    const currentFilteredSuppliers = getAllFilteredSuppliers();
    setSelectedSuppliers(prev => {
      const allFilteredIds = currentFilteredSuppliers.map(s => s._id!).filter(Boolean);
      const allSelected = allFilteredIds.every(id => prev.includes(id));
      
      if (allSelected) {
        // Deselect all filtered suppliers
        return prev.filter(id => !allFilteredIds.includes(id));
      } else {
        // Select all filtered suppliers (merge with existing selection)
        const newSelection = [...prev];
        allFilteredIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      }
    });
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (selectedSuppliers.length === 0) {
      newErrors.suppliers = 'Mindestens ein Lieferant muss ausgewählt werden';
    }

    if (!formData.deadline) {
      newErrors.deadline = 'Deadline ist erforderlich';
    } else {
      const deadlineDate = new Date(formData.deadline);
      if (deadlineDate <= new Date()) {
        newErrors.deadline = 'Deadline muss in der Zukunft liegen';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // **NEW: Check for duplicates first**
    await checkForDuplicates();
  };

  // **NEW: Check for duplicate submissions**
  const checkForDuplicates = async () => {
    try {
      setCheckingDuplicates(true);
      setErrors({});

      console.log('🔍 Checking duplicates with configs:', {
        suppliers: selectedSuppliers.length,
        conformityEnabled: conformityConfig.enabled,
        emergencyContactEnabled: emergencyContactConfig.enabled,
        templateId: formData.questionnaireTemplateId
      });

      const response = await checkDuplicateSubmissions(
        selectedSuppliers,
        formData.questionnaireTemplateId,
        new Date().getFullYear(),
        conformityConfig.enabled, // Pass conformity declaration flag
        emergencyContactConfig.enabled // Pass emergency contact flag
      );

      if (response.data.hasDuplicates) {
        setDuplicateWarnings(response.data.duplicates);
        setShowDuplicateWarning(true);
      } else {
        // No duplicates, proceed directly
        await sendQuestionnaire();
      }
    } catch (error: any) {
      console.error('Error checking duplicates:', error);
      // If duplicate check fails, continue anyway
      await sendQuestionnaire();
    } finally {
      setCheckingDuplicates(false);
    }
  };

  // **NEW: Handle proceeding despite duplicates**
  const handleProceedWithDuplicates = async (skipSuppliers: string[], skipConformity: string[], skipEmergencyContact: string[], skipMainTemplate: string[] = []) => {
    setShowDuplicateWarning(false);
    await sendQuestionnaire(skipSuppliers, skipConformity, skipEmergencyContact, skipMainTemplate);
  };

  // **NEW: Handle cancelling due to duplicates**
  const handleCancelDueDuplicates = () => {
    setShowDuplicateWarning(false);
    setDuplicateWarnings([]);
  };

  // **UPDATED: Actual send function**
  const sendQuestionnaire = async (skipSuppliers: string[] = [], skipConformity: string[] = [], skipEmergencyContact: string[] = [], skipMainTemplate: string[] = []) => {
    try {
      setSending(true);
      setErrors({});
      
      // Filter out skipped suppliers
      const suppliersToSend = selectedSuppliers.filter(id => !skipSuppliers.includes(id));
      
      const sendData: ExtendedBulkSendData = {
        ...formData,
        supplierIds: suppliersToSend,
        skipSuppliers,
        skipConformity,
        skipEmergencyContact,
        skipMainTemplate
      };

      // **NEW: Add conformity declaration if checked**
      if (conformityConfig.enabled) {
        sendData.sendConformityDeclaration = true;
        sendData.conformityDeadline = formData.deadline; // Use same deadline as main questionnaire
      }

      // **NEW: Add emergency contact if checked**
      if (emergencyContactConfig.enabled) {
        sendData.sendEmergencyContact = true;
        sendData.emergencyContactDeadline = formData.deadline; // Use same deadline as main questionnaire
      }

      console.log('📤 Sending questionnaire via bulk-send:', {
        template: template.title.de || template.title.en || 'Untitled',
        suppliers: suppliersToSend.length,
        deadline: formData.deadline,
        conformity: conformityConfig.enabled,
        emergencyContact: emergencyContactConfig.enabled,
        skipSuppliers: skipSuppliers.length,
        skipConformity: skipConformity.length,
        skipEmergencyContact: skipEmergencyContact.length,
        skipMainTemplate: skipMainTemplate.length
      });

      const response = await bulkSendQuestionnaires(sendData);

      const responseData: any = response.data;

      // **ASYNC HINTERGRUND-VERSAND: Server antwortet sofort mit jobId → Ergebnis pollen**
      if (responseData?.async && responseData?.jobId) {
        await pollBulkSend(responseData.jobId, responseData.total ?? suppliersToSend.length);
        return;
      }

      const successfulCount = Array.isArray(responseData.successful) ? responseData.successful.length : 0;
      const failedCount = Array.isArray(responseData.failed) ? responseData.failed.length : 0;
      finishSend(successfulCount, failedCount);

    } catch (error: any) {
      console.error('❌ Error sending questionnaire:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Fehler beim Versenden des Fragebogens';
      setErrors({ general: errorMessage });
      showError('Versand fehlgeschlagen', errorMessage);
    } finally {
      setSending(false);
      setBulkProgress(null);
    }
  };

  // Fortschritt des Hintergrund-Versands abfragen, bis der Job fertig ist
  const pollBulkSend = async (jobId: string, total: number): Promise<void> => {
    setBulkProgress({ processed: 0, total });

    await new Promise<void>((resolve) => {
      const interval = setInterval(async () => {
        try {
          const s = (await getBulkSendStatus(jobId)).data;
          setBulkProgress({ processed: s.processed, total: s.total });

          if (s.status === 'completed') {
            clearInterval(interval);
            finishSend(s.successful, s.failed);
            resolve();
          } else if (s.status === 'failed') {
            clearInterval(interval);
            const errorMessage = s.error || 'Fehler beim Versenden der Fragebögen';
            setErrors({ general: errorMessage });
            showError('Versand fehlgeschlagen', errorMessage);
            resolve();
          }
        } catch (pollError) {
          clearInterval(interval);
          const errorMessage = 'Status des Versands konnte nicht abgerufen werden. Der Versand läuft ggf. im Hintergrund weiter.';
          setErrors({ general: errorMessage });
          showError('Fehler', errorMessage);
          resolve();
        }
      }, 1500);
    });
  };

  const finishSend = (successfulCount: number, failedCount: number) => {
      if (successfulCount > 0) {
        console.log(`✅ Fragebogen erfolgreich an ${successfulCount} Lieferanten versendet`);
        
        // Show success toast
        const addOns = [];
        if (conformityConfig.enabled) addOns.push('Konformitätserklärung');
        if (emergencyContactConfig.enabled) addOns.push('Notfallkontakt');
        const addOnText = addOns.length > 0 ? ` (inkl. ${addOns.join(' + ')})` : '';
        
        showSuccess(
          'Fragebögen erfolgreich versendet',
          `${successfulCount} Fragebogen${successfulCount > 1 ? '' : ''} erfolgreich versendet${addOnText}`
        );
        
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        const errorMessage = `Keine Fragebögen konnten versendet werden. ${failedCount > 0 ? `Fehler bei ${failedCount} Lieferanten.` : ''}`;
        setErrors({ general: errorMessage });
        showError('Versand fehlgeschlagen', errorMessage);
      }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.01 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.99 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.99 }}
          transition={{ duration: 0.02 }}
          className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* **COMPACT HEADER** */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faPaperPlane} className="text-purple-500 mr-2 text-xs" />
                Fragebogen versenden
              </h2>
              <p className="text-xs text-gray-600 mt-0.5 truncate">
                {template.title.de || template.title.en || 'Untitled'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </div>
          
          {/* **COMPACT CONTENT** */}
          <div className="flex max-h-[calc(85vh-120px)]">
            {/* **LEFT: MINIMAL FORM** */}
            <div className="w-2/5 p-3 border-r border-gray-200 bg-gray-50">
              <h3 className="text-xs font-medium text-gray-900 mb-3">Einstellungen</h3>
              
              {errors.general && (
                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-1 text-xs" />
                    <span className="text-xs text-red-700">{errors.general}</span>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {/* **DEADLINE ONLY** */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Deadline <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    min={getMinDate()}
                    value={formData.deadline}
                    onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                    className={`w-full px-2 py-1.5 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                      errors.deadline ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.deadline && (
                    <p className="text-red-500 text-xs mt-1">{errors.deadline}</p>
                  )}
                </div>

                {/* **NOTES** */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Notizen
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Zusätzliche Informationen..."
                    rows={3}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                  />
                </div>

                {/* **NEW: CONFORMITY DECLARATION SECTION** */}
                <ConformityDeclarationSection
                  config={conformityConfig}
                  onConfigChange={setConformityConfig}
                  deadline={formData.deadline}
                  disabled={sending}
                  language={formData.language}
                />

                {/* **NEW: EMERGENCY CONTACT SECTION** */}
                <EmergencyContactSection
                  config={emergencyContactConfig}
                  onConfigChange={setEmergencyContactConfig}
                  deadline={formData.deadline}
                  disabled={sending}
                  language={formData.language}
                />
              </div>
            </div>

            {/* **RIGHT: SUPPLIER SELECTION** */}
            <div className="w-3/5 p-3">
              {suppliersLoading ? (
                <div className="text-center py-8">
                  <LoadingSpinner size="small" text="Lieferanten werden geladen..." />
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-medium text-gray-900">
                      Lieferanten ({selectedSuppliers.length}/{safeSuppliers.length})
                      {searchTerm && (
                        <span className="text-gray-500 ml-1">
                          • {getAllFilteredSuppliers().length} gefiltert (zeige {Math.min(filteredSuppliers().length, 20)})
                        </span>
                      )}
                    </h3>
                    <button
                      onClick={handleSelectAll}
                      className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded font-medium"
                    >
                      <FontAwesomeIcon icon={faUsers} className="mr-1" />
                      {(() => {
                        const allFiltered = getAllFilteredSuppliers();
                        const allFilteredSelected = allFiltered.length > 0 && 
                          allFiltered.every(s => selectedSuppliers.includes(s._id!));
                        return allFilteredSelected ? 'Alle ab' : 'Alle';
                      })()}
                    </button>
                  </div>

                  {/* **SUCHFELD** */}
                  <div className="relative mb-3">
                    <FontAwesomeIcon icon={faSearch} className="absolute left-2 top-2 text-gray-400 text-xs" />
                    <input
                      type="text"
                      placeholder="Lieferanten suchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-7 pr-3 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  {errors.suppliers && (
                    <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                      {errors.suppliers}
                    </div>
                  )}

                  <div className="max-h-72 overflow-y-auto space-y-1.5">
                    {safeSuppliers.length === 0 ? (
                      <div className="text-center py-6 text-gray-500">
                        <FontAwesomeIcon icon={faUsers} className="text-2xl mb-2" />
                        <p className="text-xs">Keine Lieferanten vorhanden</p>
                      </div>
                    ) : getAllFilteredSuppliers().length === 0 ? (
                      <div className="text-center py-6 text-gray-500">
                        <FontAwesomeIcon icon={faSearch} className="text-2xl mb-2" />
                        <p className="text-xs">Keine Lieferanten gefunden</p>
                        <p className="text-xs">Versuchen Sie einen anderen Suchbegriff</p>
                      </div>
                    ) : (
                      filteredSuppliers().map((supplier) => (
                        <SupplierItem
                          key={supplier._id}
                          supplier={supplier}
                          isSelected={selectedSuppliers.includes(supplier._id!)}
                          onToggle={() => handleSupplierToggle(supplier._id!)}
                          allSuppliers={suppliers}
                        />
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* **COMPACT FOOTER** */}
          <div className="flex gap-2 p-3 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 font-medium transition-colors text-xs"
              disabled={sending}
            >
              Abbrechen
            </button>
            <button
              onClick={handleSubmit}
              disabled={selectedSuppliers.length === 0 || !formData.deadline || sending || checkingDuplicates}
              className="flex-1 px-3 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors text-xs"
            >
              {checkingDuplicates ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                  Prüfe Duplikate...
                </>
              ) : sending ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                  {bulkProgress
                    ? `Wird gesendet... ${bulkProgress.processed}/${bulkProgress.total}`
                    : 'Wird gesendet...'}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} className="mr-1" />
                  An {selectedSuppliers.length} senden
                </>
              )}
            </button>
          </div>
        </motion.div>

        {/* **NEW: DUPLICATE WARNING MODAL** */}
        <DuplicateWarningModal
          isOpen={showDuplicateWarning}
          duplicates={duplicateWarnings}
          year={new Date().getFullYear()}
          templateName={template.title.de || template.title.en || 'Fragebogen'}
          hasConformityDuplicates={conformityConfig.enabled}
          hasEmergencyContactDuplicates={emergencyContactConfig.enabled}
          onClose={handleCancelDueDuplicates}
          onProceed={handleProceedWithDuplicates}
          onCancel={handleCancelDueDuplicates}
        />
      </motion.div>
    </AnimatePresence>
  );
};

export default SendModal;